import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ClickOutside from '~/components/ClickOutside';
import * as MultiGridStyles from '~/components/MultiGrid/styles';
import { SAME_WIDTH_MODIFIER } from '~/constants/popper';
import { useCustomPopper } from '~/hooks/popper';
import { useBoolean } from '~/hooks/useBoolean';
import SortArrowIcon from '~/icons/SortArrowIcon';
import FilterOptionsDropdown from './FilterOptionsDropdown';

const SAME_WIDTH_POPPER_OPTIONS = Object.freeze({
  strategy: 'fixed',
  placement: 'bottom-start',
  modifiers: [SAME_WIDTH_MODIFIER],
});

interface IHeaderContentProps {
  fieldValue: string;
  fieldLabel: string;
  showFilter: boolean;
  isSortable: boolean;
}

const HeaderContent = ({ fieldValue, fieldLabel, showFilter, isSortable }: IHeaderContentProps) => {
  const [areFilterOptionsShown, filterOptionsShownActions] = useBoolean();
  const popperAttributes = useCustomPopper(SAME_WIDTH_POPPER_OPTIONS);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const currentSort = searchParams.get('sort');
  let currentSortField = '';
  let currentSortDirection = '';

  if (currentSort?.includes(':')) {
    [currentSortField, currentSortDirection] = currentSort.split(':');
  }

  function handleSort(sortKeyName: string) {
    const searchParams = new URLSearchParams(location.search);
    const currentSort = searchParams.get('sort'); // e.g., 'name:asc'

    let newSort: string;

    if (currentSort?.startsWith(`${sortKeyName}:`)) {
      const currentDirection = currentSort.split(':')[1];
      const nextDirection = currentDirection === 'desc' ? 'asc' : 'desc';
      newSort = `${sortKeyName}:${nextDirection}`;
    } else {
      newSort = `${sortKeyName}:desc`;
    }

    searchParams.set('sort', newSort);
    navigate({ search: searchParams.toString() });
  }

  return (
    <MultiGridStyles.HeaderWrapper
      className={`spread full-dimension ${isSortable && 'cursor-pointer'}`}
      isActive={areFilterOptionsShown}
      {...popperAttributes.reference}
      onClick={() => {
        isSortable ? handleSort(fieldValue) : null;
      }}
    >
      {fieldLabel}
      <div>
        {isSortable && (
          <MultiGridStyles.HeaderIconWrapper
            className="center"
            onClick={() => handleSort(fieldValue)}
          >
            <SortArrowIcon
              direction={
                currentSortField === fieldValue && currentSortDirection === 'desc' ? 'desc' : 'asc'
              }
            />
          </MultiGridStyles.HeaderIconWrapper>
        )}
        {showFilter && (
          <MultiGridStyles.HeaderIconWrapper
            className="cursor-pointer center"
            onClick={filterOptionsShownActions.toggle}
            isActive={areFilterOptionsShown}
          >
            <span className="bold">Filter</span>
            {areFilterOptionsShown && (
              <ClickOutside onClose={filterOptionsShownActions.off}>
                <FilterOptionsDropdown
                  fieldValue={fieldValue}
                  fixedAttributes={popperAttributes.fixed}
                />
              </ClickOutside>
            )}
          </MultiGridStyles.HeaderIconWrapper>
        )}
      </div>
    </MultiGridStyles.HeaderWrapper>
  );
};

export default HeaderContent;
